import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger
} from '@/components/ui/alert-dialog';
import {
  Calendar,
  Trash2,
  Edit2,
  ClipboardList,
  User,
  Building2,
  AlertTriangle,
  Flag,
  Clock,
  Users,
  Crown,
} from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useOrdenesTrabajoContext, TecnicoAsignado } from '@/contexts/OrdenesTrabajoContext';
import { DocumentacionIndicator } from './DocumentacionIndicator';
import { OrdenTrabajoEditForm } from './OrdenTrabajoEditForm';
import { AsignarTecnicosPopover } from './AsignarTecnicosPopover';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { toast } from 'sonner';

import { OrdenTrabajo } from '@/contexts/OrdenesTrabajoContext';

interface Tecnico {
  user_id: string;
  nombre: string;
  avatar_url?: string | null;
}

interface Filtros {
  busqueda: string;
  estado: string;
  prioridad: string;
  tecnico: string;
}

interface OrdenesListaViewProps {
  ordenes: OrdenTrabajo[];
  filtros: Filtros;
  /** Mostrar badges "Falta técnico/fecha/dirección" para guiar al gestor en la planificación */
  showFaltantes?: boolean;
  /** Lista de técnicos para el popover de asignación rápida */
  tecnicos?: Tecnico[];
  /** Mensaje de estado vacío personalizado */
  emptyMessage?: { title: string; subtitle?: string };
}

function getFaltantes(orden: OrdenTrabajo): string[] {
  const faltan: string[] = [];
  const tieneTecnico = !!orden.tecnico_asignado_id || (orden.tecnicos_asignados && orden.tecnicos_asignados.length > 0);
  if (!tieneTecnico) faltan.push('técnico');
  if (!orden.fecha_limite) faltan.push('fecha');
  const direccionCliente = orden.cliente?.direccion;
  if (!direccionCliente) faltan.push('dirección');
  return faltan;
}

const estadoColors: Record<string, string> = {
  creada: 'bg-muted text-muted-foreground border-muted',
  asignada: 'bg-blue-500/20 text-blue-600 dark:text-blue-400 border-blue-500/30',
  en_progreso: 'bg-purple-500/20 text-purple-600 dark:text-purple-400 border-purple-500/30',
  finalizada: 'bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 border-emerald-500/30',
  cancelada: 'bg-red-500/20 text-red-600 dark:text-red-400 border-red-500/30',
};

const estadoLabels: Record<string, string> = {
  creada: 'Creada',
  asignada: 'Asignada',
  en_progreso: 'En Progreso',
  finalizada: 'Finalizada',
  cancelada: 'Cancelada',
};

const accentByEstado: Record<string, string> = {
  creada: 'border-l-muted-foreground/40',
  asignada: 'border-l-blue-500',
  en_progreso: 'border-l-purple-500',
  finalizada: 'border-l-emerald-500',
  cancelada: 'border-l-red-500',
};

const prioridadColors: Record<string, string> = {
  urgente: 'bg-red-500/20 text-red-600 dark:text-red-400 border-red-500/30',
  alta: 'bg-orange-500/20 text-orange-600 dark:text-orange-400 border-orange-500/30',
  media: 'bg-yellow-500/20 text-yellow-700 dark:text-yellow-400 border-yellow-500/30',
  baja: 'bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 border-emerald-500/30',
};

const prioridadLabels: Record<string, string> = {
  urgente: 'Urgente',
  alta: 'Alta',
  media: 'Media',
  baja: 'Baja',
};

export const OrdenesListaView: React.FC<OrdenesListaViewProps> = ({ ordenes, filtros, showFaltantes = false, tecnicos = [], emptyMessage }) => {
  const { deleteOrden, refreshOrdenes, assignTecnicos } = useOrdenesTrabajoContext();
  const [selectedOrden, setSelectedOrden] = useState<OrdenTrabajo | null>(null);
  const [showEditForm, setShowEditForm] = useState(false);

  // Apply filters
  const ordenesFiltradas = ordenes.filter(orden => {
    const clienteNombre = orden.cliente?.nombre || orden.cliente_nombre || '';
    if (filtros.busqueda &&
      !orden.titulo.toLowerCase().includes(filtros.busqueda.toLowerCase()) &&
      !orden.numero_ot.toLowerCase().includes(filtros.busqueda.toLowerCase()) &&
      !clienteNombre.toLowerCase().includes(filtros.busqueda.toLowerCase())) {
      return false;
    }
    if (filtros.estado && filtros.estado !== 'all' && orden.estado !== filtros.estado) return false;
    if (filtros.prioridad && filtros.prioridad !== 'all' && orden.prioridad !== filtros.prioridad) return false;
    if (filtros.tecnico && filtros.tecnico !== 'all') {
      if (filtros.tecnico === 'sin_asignar' && orden.tecnico_asignado_id) return false;
      if (filtros.tecnico !== 'sin_asignar' && orden.tecnico_asignado_id !== filtros.tecnico) return false;
    }
    return true;
  });

  const handleDeleteOrden = async (ordenId: string) => {
    try {
      await deleteOrden(ordenId);
      toast.success('Orden eliminada correctamente');
    } catch (error) {
      toast.error('Error al eliminar la orden');
    }
  };

  const handleEditSuccess = async () => {
    setShowEditForm(false);
    setSelectedOrden(null);
    await refreshOrdenes();
  };

  const openEdit = (orden: OrdenTrabajo) => {
    setSelectedOrden(orden);
    setShowEditForm(true);
  };

  if (ordenesFiltradas.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground border border-dashed border-border/60 rounded-lg bg-card/30">
        <ClipboardList className="w-12 h-12 mx-auto mb-4 opacity-50" />
        <p>{emptyMessage?.title || 'No hay órdenes que coincidan con los filtros'}</p>
        <p className="text-sm">{emptyMessage?.subtitle || 'Ajusta la búsqueda o crea una nueva OT'}</p>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-3">
        {ordenesFiltradas.map((orden) => {
          const clienteNombre = orden.cliente?.nombre || orden.cliente_nombre || 'Sin cliente';
          const accentClass = accentByEstado[orden.estado] || 'border-l-muted';
          const isUrgente = orden.prioridad === 'urgente';

          return (
            <Card
              key={orden.id}
              className={`border-l-4 ${accentClass} transition-colors hover:bg-muted/40 cursor-pointer`}
              onClick={() => openEdit(orden)}
            >
              <CardContent className="p-4 space-y-3">
                {/* Header: número + título + estado */}
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex items-start gap-3 min-w-0 flex-1">
                    <div className="mt-0.5 shrink-0">
                      {isUrgente ? (
                        <AlertTriangle className="w-4 h-4 text-destructive" />
                      ) : (
                        <ClipboardList className="w-4 h-4 text-primary" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-sm text-muted-foreground">
                          {orden.numero_ot}
                        </span>
                        <span className="font-semibold truncate">{orden.titulo}</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5 flex-wrap">
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {clienteNombre}
                        </span>
                        {orden.obra?.nombre && (
                          <>
                            <span>·</span>
                            <span className="flex items-center gap-1">
                              <Building2 className="w-3 h-3" />
                              {orden.obra.nombre}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge variant="outline" className={`text-xs ${prioridadColors[orden.prioridad]}`}>
                      <Flag className="w-3 h-3 mr-1" />
                      {prioridadLabels[orden.prioridad]}
                    </Badge>
                    <Badge variant="outline" className={`text-xs ${estadoColors[orden.estado]}`}>
                      {estadoLabels[orden.estado]}
                    </Badge>
                  </div>
                </div>

                {/* Meta: fecha + técnicos asignados */}
                <div className="flex items-center gap-x-4 gap-y-2 text-sm text-muted-foreground flex-wrap">
                  {orden.fecha_limite && (
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3.5 h-3.5" />
                      {format(new Date(orden.fecha_limite), "dd 'de' MMM yyyy", { locale: es })}
                    </span>
                  )}
                  {orden.hora_ejecucion && (
                    <span className="flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5" />
                      {orden.hora_ejecucion.substring(0, 5)}
                    </span>
                  )}
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <Users className="w-3.5 h-3.5" />
                    {(orden.tecnicos_asignados || []).length > 0 ? (
                      orden.tecnicos_asignados!.map((t) => (
                        <span
                          key={t.tecnico_id}
                          className="inline-flex items-center gap-1 bg-muted/60 border border-border rounded-full pl-0.5 pr-2 py-0.5 text-xs"
                          title={t.nombre}
                        >
                          <Avatar className="h-4 w-4">
                            <AvatarImage src={t.avatar_url || undefined} />
                            <AvatarFallback className="text-[8px]">
                              {(t.nombre || '?').split(' ').map(p => p[0]).join('').slice(0, 2).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <span className="max-w-[100px] truncate">{(t.nombre || '').split(' ')[0]}</span>
                          {t.rol === 'principal' && <Crown className="w-3 h-3 text-primary" />}
                        </span>
                      ))
                    ) : (
                      <span className="italic text-xs">Sin asignar</span>
                    )}
                  </div>
                </div>

                {/* Faltantes para planificación */}
                {showFaltantes && (() => {
                  const faltan = getFaltantes(orden);
                  if (faltan.length === 0) return null;
                  return (
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs text-muted-foreground">Falta:</span>
                      {faltan.map((f) => (
                        <Badge
                          key={f}
                          variant="outline"
                          className="text-xs bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30"
                        >
                          {f}
                        </Badge>
                      ))}
                    </div>
                  );
                })()}

                {/* Descripción */}
                {orden.descripcion && (
                  <p className="text-sm line-clamp-2 text-foreground/80">
                    {orden.descripcion}
                  </p>
                )}

                {/* Footer: docs + acciones */}
                <div className="flex items-center justify-between gap-2 flex-wrap pt-1">
                  <div onClick={(e) => e.stopPropagation()}>
                    <DocumentacionIndicator
                      partesCount={orden.partes_count || 0}
                      jornadasCount={orden.jornadas_count || 0}
                      estado={orden.estado}
                      variant="compact"
                      ordenId={orden.id}
                      partesData={orden.partes_details || []}
                      jornadasData={orden.jornadas_details || []}
                    />
                  </div>
                  <div className="flex items-center gap-2 ml-auto" onClick={(e) => e.stopPropagation()}>
                    {tecnicos.length > 0 && (
                      <AsignarTecnicosPopover
                        tecnicos={tecnicos}
                        initialAsignados={orden.tecnicos_asignados || []}
                        onConfirm={async (asignados) => {
                          try {
                            await assignTecnicos(orden.id, asignados);
                          } catch (e) {
                            // toast already shown in context
                          }
                        }}
                      />
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      className="gap-1"
                      onClick={() => openEdit(orden)}
                    >
                      <Edit2 className="w-3.5 h-3.5" />
                      <span className="hidden sm:inline">Editar</span>
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="sm" variant="outline" className="gap-1 text-destructive hover:text-destructive">
                          <Trash2 className="w-3.5 h-3.5" />
                          <span className="hidden sm:inline">Eliminar</span>
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>¿Eliminar orden?</AlertDialogTitle>
                          <AlertDialogDescription>
                            Esta acción no se puede deshacer. Se eliminará permanentemente la orden {orden.numero_ot}.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleDeleteOrden(orden.id)}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          >
                            Eliminar
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {selectedOrden && (
        <OrdenTrabajoEditForm
          orden={selectedOrden}
          isOpen={showEditForm}
          onClose={() => {
            setShowEditForm(false);
            setSelectedOrden(null);
          }}
          onSuccess={handleEditSuccess}
        />
      )}
    </>
  );
};
